import { ROUTES } from "../constants";

const safeHasPermission = (hasPermission, key) => {
	if (typeof hasPermission !== "function" || !key) return false;
	try {
		return !!hasPermission(key);
	} catch {
		return false;
	}
};

const settingsPermissionKeys = [
	"app_settings.manage_settings",
	"app_settings.manage_order_settings",
	"app_settings.manage_inventory_settings",
	"app_settings.manage_site_settings",
	"app_settings.manage_reminder_settings",
	"app_settings.manage_cart_retention",
];

const isAdminAnalyticsRole = (user) => {
	const roleName = String(user?.role?.role_name || user?.user_role_name || user?.user_role || "").trim().toLowerCase();
	const roleType = String(user?.role?.role_type || "").trim().toLowerCase();
	return Number(user?.user_id) === 1 || roleName === "admin" || roleType === "admin";
};

export const canAccessOrderAnalytics = (hasPermission, user) =>
	Number(user?.user_id) === 1 ||
	(
		isAdminAnalyticsRole(user) &&
		(
			safeHasPermission(hasPermission, "analytics.view_order_analytics") ||
			safeHasPermission(hasPermission, "analytics")
		)
	);

export const canAccessUserAnalytics = (hasPermission, user) =>
	Number(user?.user_id) === 1 ||
	(
		isAdminAnalyticsRole(user) &&
		(
			safeHasPermission(hasPermission, "analytics.view_user_analytics") ||
			safeHasPermission(hasPermission, "analytics")
		)
	);

export const canAccessAnalytics = (hasPermission, user) =>
	canAccessOrderAnalytics(hasPermission, user) || canAccessUserAnalytics(hasPermission, user);

export const canAccessServices = (hasPermission) =>
	safeHasPermission(hasPermission, "service_management.view_cases") ||
	safeHasPermission(hasPermission, "service_management.view_workorders");

export const canAccessCart = (hasPermission) =>
	safeHasPermission(hasPermission, "order_management.add_to_cart");

export const canAccessRecommendations = (hasPermission) =>
	safeHasPermission(hasPermission, "recommendations_management.add_parts") ||
	safeHasPermission(hasPermission, "recommendations_management.create_list") ||
	safeHasPermission(hasPermission, "recommendations_management.edit_list") ||
	safeHasPermission(hasPermission, "recommendations_management.delete_list");

export const canAccessRequests = (hasPermission) =>
	safeHasPermission(hasPermission, "requests_management.allow_notify_customer_care");

export const canAccessOrderHistory = (hasPermission) =>
	safeHasPermission(hasPermission, "order_management.view_order_history") ||
	safeHasPermission(hasPermission, "order_management.create_order_schedules");

export const canAccessSettings = (user) => user?.user_id === 1;

export const canAccessChatbot = (hasPermission, user) =>
	Number(user?.user_id) === 1 ||
	safeHasPermission(hasPermission, "app_settings.access_chatbot");

export const canAccessAdmin = (hasPermission, user) => {
	const isSuperAdmin = user?.user_id === 1;
	const checks = [
		() => safeHasPermission(hasPermission, "user_management.create_users") || safeHasPermission(hasPermission, "user_management.edit_users") || safeHasPermission(hasPermission, "user_management.delete_users"),
		() => safeHasPermission(hasPermission, "role_management.create_roles") || safeHasPermission(hasPermission, "role_management.edit_roles") || safeHasPermission(hasPermission, "role_management.delete_roles"),
		() => safeHasPermission(hasPermission, "site_management.edit_sites"),
		() => safeHasPermission(hasPermission, "document_management.create_documents") || safeHasPermission(hasPermission, "document_management.edit_documents") || safeHasPermission(hasPermission, "document_management.delete_documents"),
		() => safeHasPermission(hasPermission, "machine_provision_management.provision_machines") || safeHasPermission(hasPermission, "machine_provision_management.edit_provisions") || safeHasPermission(hasPermission, "machine_provision_management.delete_provisions"),
		() => safeHasPermission(hasPermission, "app_settings.allow_model_uploads"),
		() => safeHasPermission(hasPermission, "order_management.create_order_schedules") || safeHasPermission(hasPermission, "order_management.edit_order_schedules") || safeHasPermission(hasPermission, "order_management.delete_order_schedules"),
		() => settingsPermissionKeys.some((perm) => safeHasPermission(hasPermission, perm)),
		() => safeHasPermission(hasPermission, "app_settings.manage_price_markups"),
		() => isSuperAdmin,
	];
	return checks.some((fn) => {
		try {
			return !!fn();
		} catch {
			return false;
		}
	});
};

export const filterPageRoutesByAccess = (routes, hasPermission, user) =>
	routes.filter((page) => {
		switch (page.href) {
			case ROUTES.ANALYTICS:
				return canAccessOrderAnalytics(hasPermission, user);
			case ROUTES.USER_ANALYTICS:
				return canAccessUserAnalytics(hasPermission, user);
			case ROUTES.SERVICES:
				return canAccessServices(hasPermission);
			case ROUTES.ADMIN:
				return canAccessAdmin(hasPermission, user);
			case ROUTES.ORDER_HISTORY:
				return canAccessOrderHistory(hasPermission);
			case ROUTES.CART:
			case ROUTES.TRACK_ORDER:
				return canAccessCart(hasPermission);
			case ROUTES.RSPL:
			case ROUTES.RECOMMENDATIONS:
				return canAccessRecommendations(hasPermission);
			case ROUTES.REQUESTS:
				return canAccessRequests(hasPermission);
			case ROUTES.SAVED_FOR_LATER:
				return canAccessCart(hasPermission);
			case ROUTES.SETTINGS:
				return canAccessSettings(user);
			default:
				return true;
		}
	});
